import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Tooltip,
  Divider
} from '@mui/material';

// Alert type icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';

const alertTypeInfo = {
  priceAlert: {
    title: 'Price Alert',
    description: 'Get notified when stocks or crypto hit price targets or make significant moves',
    icon: <TrendingUpIcon fontSize="large" />,
    color: '#4CAF50', // green
    requiredPlan: 'free'
  },
  newsAlert: {
    title: 'News Alert',
    description: 'Stay informed about breaking news for your selected companies or keywords',
    icon: <ArticleIcon fontSize="large" />,
    color: '#2196F3', // blue
    requiredPlan: 'free'
  },
  congressTrade: {
    title: 'Congressional Trading Alert',
    description: 'Get notifications when Congress members trade stocks you care about',
    icon: <AccountBalanceIcon fontSize="large" />,
    color: '#9C27B0', // purple
    requiredPlan: 'basic'
  },
  insiderTrade: {
    title: 'Insider Trading Alert',
    description: 'Be alerted when company executives buy or sell their own stock',
    icon: <BusinessIcon fontSize="large" />,
    color: '#FF9800', // orange
    requiredPlan: 'basic'
  },
  marketEvent: {
    title: 'Market Events',
    description: 'Get notified about earnings announcements and other market events',
    icon: <EventIcon fontSize="large" />,
    color: '#F44336', // red
    requiredPlan: 'premium'
  }
};

const AlertTypeSelection = ({ selectedType, onSelectType, availableTypes, userPlan }) => {
  const isTypeAvailable = (type) => {
    return availableTypes.includes(type);
  };

  const getPlanBadge = (type) => {
    const planName = alertTypeInfo[type]?.requiredPlan;
    if (!planName || planName === 'free') return null;

    return planName.charAt(0).toUpperCase() + planName.slice(1);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Select Alert Type
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Choose the type of alert you want to create. Different alerts monitor different aspects of the market.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Object.keys(alertTypeInfo).map((type) => {
          const info = alertTypeInfo[type];
          const available = isTypeAvailable(type);
          const planBadge = getPlanBadge(type);

          return (
            <Grid item xs={12} sm={6} lg={4} key={type}>
              <Tooltip
                title={!available ? `Requires ${info.requiredPlan} plan or higher` : ''}
                placement="top"
              >
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    opacity: available ? 1 : 0.7,
                    transform: selectedType === type ? 'scale(1.02)' : 'scale(1)',
                    border: selectedType === type ? `2px solid ${info.color}` : 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: available ? 'scale(1.02)' : 'scale(1)',
                    }
                  }}
                >
                  <CardActionArea
                    sx={{ height: '100%', p: 2 }}
                    onClick={() => available && onSelectType(type)}
                    disabled={!available}
                  >
                    {planBadge && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: planBadge === 'Premium' ? 'secondary.main' : 'primary.main',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          zIndex: 2
                        }}
                      >
                        {planBadge}
                      </Box>
                    )}

                    {!available && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          p: 1
                        }}
                      >
                        <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        color: info.color
                      }}
                    >
                      {info.icon}
                      {selectedType === type && (
                        <StarIcon
                          fontSize="small"
                          sx={{ ml: 1, color: info.color }}
                        />
                      )}
                    </Box>

                    <Typography variant="h6" component="h3" gutterBottom>
                      {info.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {info.description}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}

        <Grid item xs={12} sm={6} lg={4}>
          <Card sx={{
            height: '100%',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            border: '1px dashed rgba(0, 0, 0, 0.12)'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                More alert types coming soon!
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AlertTypeSelection;